import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  AlertCircle, 
  Loader2, 
  DollarSign, 
  Package, 
  TrendingUp, 
  BarChart3,
  Check,
  Copy,
  Shield,
  Calendar,
  Eye,
  ArrowRight,
  Download,
  FileText
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { apiGet, ApiError } from '../lib/apiClient';

const SharedReport = () => {
  const { shareToken } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, [shareToken]);

  const generatePDF = async () => {
    if (!report) return;
    
    setPdfLoading(true);
    toast.info('Generating professional PDF report...');
    
    try {
      const { generateAnalysisPdf, generateFinancialPdf } = await import('../utils/pdfGenerator');
      
      let pdf;
      const analysis = report.analysis || report;
      
      if (analysis.type === 'financial_impact') {
        pdf = await generateFinancialPdf(analysis);
      } else {
        pdf = await generateAnalysisPdf(analysis, analysis.results || []);
      }
      
      const fileName = `Decoded_Shared_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const data = await apiGet(`/api/report/${shareToken}`, { auth: false });
      setReport(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setError('This shared report has expired.');
      } else if (err instanceof ApiError && err.status === 404) {
        setError('Report not found.');
      } else {
        setError('Failed to load report.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success('HS Code copied!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0B3D91] to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/80">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0B3D91] to-slate-900 flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-xl rounded-2xl p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Report Unavailable</h1>
          <p className="text-white/70 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-white text-[#0B3D91] hover:bg-white/90">
              Go to Decoded
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const analysis = report.analysis;
  const results = analysis.results || [];
  
  // Calculate stats
  const complianceRiskItems = results.filter(r => r.tariff_delta < 0 && r.current_hs_code);
  const savingsItems = results.filter(r => r.tariff_delta > 0);
  const totalSavings = results.reduce((sum, r) => sum + (r.estimated_savings > 0 ? r.estimated_savings : 0), 0);
  const totalComplianceExposure = Math.abs(results.reduce((sum, r) => sum + (r.estimated_savings < 0 ? r.estimated_savings : 0), 0));
  const totalImportValue = results.reduce((sum, r) => sum + ((r.unit_cost || 0) * (r.annual_volume || 0)), 0);

  // Chart data
  const confidenceData = [
    { name: 'High (≥80%)', value: results.filter(r => r.confidence_score >= 0.8).length, color: '#10B981' },
    { name: 'Medium (50-79%)', value: results.filter(r => r.confidence_score >= 0.5 && r.confidence_score < 0.8).length, color: '#F59E0B' },
    { name: 'Low (<50%)', value: results.filter(r => r.confidence_score < 0.5).length, color: '#EF4444' }
  ].filter(d => d.value > 0);

  const impactData = results.slice(0, 10).map((r, i) => ({
    name: r.sku_id.slice(0, 8),
    impact: r.estimated_savings || 0,
    fill: r.estimated_savings > 0 ? '#10B981' : r.estimated_savings < 0 ? '#EF4444' : '#94A3B8'
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Download PDF Button - Fixed position */}
      <div className="fixed top-4 right-4 z-50 flex gap-2 print:hidden">
        <Button
          onClick={generatePDF}
          disabled={pdfLoading}
          className="bg-white text-[#0B3D91] hover:bg-slate-100 shadow-lg rounded-xl"
        >
          {pdfLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {pdfLoading ? 'Generating...' : 'Download PDF'}
        </Button>
      </div>

      <div ref={reportRef}>
      {/* Header with Branding */}
      <header className="bg-gradient-to-r from-[#0B3D91] via-[#1E5EFF] to-[#0B3D91] text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Company Logo */}
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-[#0B3D91]">D</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">DECODED</h1>
                <p className="text-blue-200 text-sm">AI-Powered Tariff Classification</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-blue-200 text-sm flex items-center gap-2 justify-end">
                <Calendar className="w-4 h-4" />
                {new Date(analysis.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </p>
              <p className="text-blue-200 text-sm flex items-center gap-2 justify-end mt-1">
                <Eye className="w-4 h-4" />
                Shared by {report.shared_by}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Report Title */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h2 className="text-2xl font-bold text-slate-900">
            Classification Analysis Report
          </h2>
          <p className="text-slate-500 mt-1">
            {results.length} product{results.length > 1 ? 's' : ''} analyzed • 
            {totalImportValue > 0 ? ` $${totalImportValue.toLocaleString()} total import value` : ' Import value not provided'}
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Executive Summary Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#2DD4BF] flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm text-slate-500 mb-1">Potential Savings</p>
            <p className="text-3xl font-bold text-[#10B981]">
              {totalSavings > 0 ? `$${totalSavings.toLocaleString()}` : '$0'}
            </p>
            <p className="text-xs text-slate-400 mt-1">{savingsItems.length} opportunities</p>
          </div>

          <div className={`bg-white rounded-2xl p-6 shadow-lg border ${complianceRiskItems.length > 0 ? 'border-[#EF4444]/30 bg-[#EF4444]/5' : 'border-slate-100'}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${complianceRiskItems.length > 0 ? 'bg-gradient-to-br from-[#EF4444] to-[#F59E0B]' : 'bg-slate-200'}`}>
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm text-slate-500 mb-1">Compliance Exposure</p>
            <p className={`text-3xl font-bold ${complianceRiskItems.length > 0 ? 'text-[#EF4444]' : 'text-slate-400'}`}>
              {totalComplianceExposure > 0 ? `$${totalComplianceExposure.toLocaleString()}` : '$0'}
            </p>
            <p className="text-xs text-slate-400 mt-1">{complianceRiskItems.length} risk items</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#60A5FA] flex items-center justify-center mb-4">
              <Package className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm text-slate-500 mb-1">Products Analyzed</p>
            <p className="text-3xl font-bold text-slate-900">{results.length}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#A5B4FC] flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm text-slate-500 mb-1">Optimization Rate</p>
            <p className="text-3xl font-bold text-slate-900">
              {results.length > 0 ? Math.round((savingsItems.length / results.length) * 100) : 0}%
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Confidence Distribution */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#2563EB]" />
              Confidence Distribution
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={confidenceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {confidenceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} SKUs`, '']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Financial Impact */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#10B981]" />
              Financial Impact by SKU
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={impactData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={(v) => `$${v.toLocaleString()}`} />
                  <YAxis type="category" dataKey="name" width={70} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Impact']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Bar dataKey="impact" radius={[0, 4, 4, 0]}>
                    {impactData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 mb-8">
          <h3 className="font-semibold text-slate-900 mb-4">Classification Results</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">SKU</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Description</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Current HS</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Correct HS</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Impact</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {results.map((sku, index) => {
                  const status = sku.tariff_delta > 0 ? 'savings' : sku.tariff_delta < 0 ? 'risk' : 'ok';
                  return (
                    <tr key={index} className={`border-b border-slate-50 ${status === 'risk' ? 'bg-[#EF4444]/5' : ''}`}>
                      <td className="py-3 px-4">
                        {status === 'risk' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-[#EF4444]/10 text-[#EF4444]">
                            <AlertCircle className="w-3 h-3" /> Risk
                          </span>
                        )}
                        {status === 'savings' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-[#10B981]/10 text-[#10B981]">
                            <TrendingUp className="w-3 h-3" /> Save
                          </span>
                        )}
                        {status === 'ok' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                            <Check className="w-3 h-3" /> OK
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-500">{sku.sku_id}</td>
                      <td className="py-3 px-4 text-sm text-slate-700 max-w-[200px] truncate">{sku.description}</td>
                      <td className="py-3 px-4 font-mono text-sm text-slate-400">{sku.current_hs_code || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-[#10B981] font-medium">{sku.suggested_hs_code}</span>
                          <button
                            onClick={() => copyToClipboard(sku.suggested_hs_code)}
                            className="p-1 rounded hover:bg-[#10B981]/10"
                          >
                            {copiedCode === sku.suggested_hs_code ? (
                              <Check className="w-3.5 h-3.5 text-[#10B981]" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-slate-400" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-semibold">
                        {status === 'risk' && <span className="text-[#EF4444]">+${Math.abs(sku.estimated_savings || 0).toLocaleString()}</span>}
                        {status === 'savings' && <span className="text-[#10B981]">-${(sku.estimated_savings || 0).toLocaleString()}</span>}
                        {status === 'ok' && <span className="text-slate-400">-</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          sku.confidence_score >= 0.8 ? 'bg-[#10B981]/10 text-[#10B981]' :
                          sku.confidence_score >= 0.5 ? 'bg-[#F59E0B]/10 text-[#F59E0B]' :
                          'bg-[#EF4444]/10 text-[#EF4444]'
                        }`}>
                          {Math.round(sku.confidence_score * 100)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <Shield className="w-8 h-8 text-[#F59E0B] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-lg mb-2">Important Disclaimer</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                This report is generated by Decoded's AI-powered classification engine for informational purposes only. 
                It does not constitute legal, customs, or trade compliance advice. The suggested HS codes and tariff 
                rates are estimates based on product descriptions and may not reflect actual rates determined by 
                U.S. Customs and Border Protection (CBP) or other customs authorities.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mt-3">
                <strong className="text-white">Before taking action:</strong> We strongly recommend consulting with a licensed 
                customs broker or trade compliance professional to verify classifications, assess compliance risks, 
                and understand the legal implications of any tariff changes. Decoded and its affiliates assume no 
                liability for decisions made based on this report.
              </p>
              <p className="text-slate-400 text-xs mt-4">
                © {new Date().getFullYear()} Decoded. All rights reserved. This report is confidential and intended 
                solely for the recipient. Unauthorized distribution is prohibited.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-slate-500 mb-4">Want to analyze your own catalog?</p>
          <Link to="/">
            <Button className="bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
              Try Decoded Free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-xl font-bold text-[#0B3D91]">D</span>
            </div>
            <span className="text-xl font-bold">DECODED</span>
          </div>
          <p className="text-slate-400 text-sm">
            AI-Powered Tariff Classification & Landed Cost Optimization
          </p>
          <p className="text-slate-500 text-xs mt-4">
            Report generated on {new Date().toLocaleDateString()} • Valid for 30 days
          </p>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default SharedReport;
